import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Modal, ModalFooter, Button, Loading, Layout } from '@folio/stripes/components';
import { FormattedMessage } from 'react-intl';
import useCubeQuery from '../../../../hooks/useQuery';
import GenericTableView from '../views/table/GenericTableView';
import ErrorMessage from '../../../common/errorMessage/ErrorMessage';
import EmptyData from '../../../common/emptyData/EmptyData';

function DrillDownModal({ isOpen, onClose, drillDownData }) {
  const [drillQuery, setDrillQuery] = useState(drillDownData?.query || null);

  useEffect(() => {
    if (drillDownData?.query) setDrillQuery(drillDownData.query);
  }, [drillDownData]);

  const [resultSet, error, isLoading] = useCubeQuery(drillQuery);

  if (!drillDownData) return null;

  const { measureName, measureValue } = drillDownData;

  const measureLabel = (measureName || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (l) => l.toUpperCase());

  const title = (
    <FormattedMessage
      id="ui-reports.drillDown.modal.title"
      values={{ measure: measureLabel, count: measureValue }}
      defaultMessage="{measure} Details ({count} records)"
    />
  );

  const subHeader = !isLoading && resultSet
    ? (
      <FormattedMessage
        id="stripes-smart-components.searchResultsCountHeader"
        values={{ count: resultSet?.loadResponses?.[0]?.total || 0 }}
      />
    )
    : null;

  const footer = (
    <ModalFooter>
      <Button buttonStyle="primary" onClick={onClose} marginBottom0>
        <FormattedMessage
          id="ui-reports.drillDown.modal.closeBtn"
          defaultMessage="Close"
        />
      </Button>
    </ModalFooter>
  );

  const renderContent = () => {
    if (isLoading) {
      return (
        <Layout className="centered full" style={{ height: '8rem' }}>
          <Loading size="xlarge" />
        </Layout>
      );
    }
    if (error) return <ErrorMessage errorMessage={error.toString()} />;
    if (!resultSet?.loadResponses?.length) return <EmptyData />;

    return (
      <GenericTableView
        resultSet={resultSet}
        query={drillQuery}
        setQuery={setDrillQuery}
        onDrillDown={null}
        fieldAliasMap={drillDownData.fieldAliasMap || {}}
        displaySettings={{ autosize: false }}
      />
    );
  };

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      size="large"
      label={title}
      subHeader={subHeader}
      footer={footer}
      dismissible
    >
      {renderContent()}
    </Modal>
  );
}

DrillDownModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  drillDownData: PropTypes.shape({
    query: PropTypes.object,
    measureName: PropTypes.string,
    measureValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    fieldAliasMap: PropTypes.object,
  }),
};

export default DrillDownModal;
